<template>
  <div class="outing-attendance-container">
    <div class="header-section">
      <div class="header-content">
        <div class="title-section">
          <div class="page-title">
            <div class="title-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 6v6l4 2" />
              </svg>
            </div>
            <h1>Outing Attendance</h1>
          </div>
        </div>
        <div class="header-actions">
          <button type="button" class="btn btn-primary" :disabled="isLoading" @click="fetchAttendance">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M23 4v6h-6M1 20v-6h6" />
              <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
            </svg>
            <span>Refresh</span>
          </button>
        </div>
      </div>
    </div>

    <div class="stats-section">
      <div class="stat-card">
        <div class="stat-icon active">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
          </svg>
        </div>
        <div class="stat-content">
          <h3>{{ totalCount }}</h3>
          <p>Total outings</p>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
        </div>
        <div class="stat-content">
          <h3>{{ uniqueGuests }}</h3>
          <p>Guests (this page)</p>
        </div>
      </div>
    </div>

    <div class="table-section">
      <div class="table-container">
        <div v-if="isLoading" class="table-loading-overlay">
          <div class="loading-content">
            <div class="spinner"></div>
            <p class="loading-text">Loading outing attendance...</p>
          </div>
        </div>

        <div class="table-wrapper">
          <table class="complaint-table">
            <thead v-if="records.length > 0 && !isLoading">
              <tr>
                <th>Guest name</th>
                <th>Room</th>
                <th>Mobile</th>
                <th>Checkout</th>
                <th>Check-in</th>
                <th>Duration</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in records" :key="row.id" class="table-row">
                <td>{{ guestDisplayName(row) }}</td>
                <td>{{ row.roomNumber || '--' }}</td>
                <td>{{ row.mobileno || '--' }}</td>
                <td>{{ formatDateTime(row.checkout_time) }}</td>
                <td>{{ formatDateTime(row.checkin_time) }}</td>
                <td>{{ formatDuration(row.duration_outside) }}</td>
                <td class="col-desc">{{ row.checkout_description || '--' }}</td>
              </tr>
              <tr v-if="records.length === 0 && !isLoading" class="empty-row">
                <td colspan="7" class="empty-message">
                  <div class="empty-state">
                    <img
                      src="@/assets/guest-maint-staff.svg"
                      alt="No outing records"
                      class="empty-illustration-img"
                    />
                    <p>No outing attendance records</p>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-if="totalCount > pageSize" class="pagination-wrapper">
        <div class="pagination-left">
          <span class="pagination-info">
            Showing {{ rangeStart }} to {{ rangeEnd }} of {{ totalCount }} entries
          </span>
          <label class="page-size-label">
            <span class="page-size-label-text">Per page</span>
            <select
              v-model.number="pageSize"
              class="page-size-select"
              :disabled="isLoading"
              @change="onPageSizeChange"
            >
              <option :value="10">10</option>
              <option :value="20">20</option>
              <option :value="50">50</option>
            </select>
          </label>
        </div>
        <div class="pagination-controls">
          <button
            type="button"
            class="pagination-btn"
            :disabled="currentPage === 1 || isLoading"
            @click="goToPage(currentPage - 1)"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <button
            v-for="page in visiblePages"
            :key="page"
            type="button"
            class="pagination-btn"
            :class="{ active: page === currentPage }"
            :disabled="isLoading"
            @click="goToPage(page)"
          >
            {{ page }}
          </button>
          <button
            type="button"
            class="pagination-btn"
            :disabled="currentPage === totalPages || isLoading"
            @click="goToPage(currentPage + 1)"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiService from '@/services/api';
import { isNoDataError, isHmsSubscriptionInactiveError } from '../utilities/errorHelper';

export default {
  name: 'OutingAttendanceView',
  props: {
    objectData: {
      type: Object,
      default: () => ({}),
    },
    objectSpaces: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      records: [],
      totalCount: 0,
      isLoading: false,
      currentPage: 1,
      pageSize: 10,
    };
  },
  computed: {
    uniqueGuests() {
      const ids = new Set(this.records.map((r) => r.guestid).filter(Boolean));
      return ids.size;
    },
    totalPages() {
      if (this.totalCount <= 0) return 1;
      return Math.ceil(this.totalCount / this.pageSize);
    },
    rangeStart() {
      if (this.totalCount === 0) return 0;
      return (this.currentPage - 1) * this.pageSize + 1;
    },
    rangeEnd() {
      if (this.totalCount === 0) return 0;
      return Math.min(this.currentPage * this.pageSize, this.totalCount);
    },
    visiblePages() {
      const pages = [];
      const maxVisible = 4;
      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);
      if (end - start < maxVisible - 1) {
        start = Math.max(1, end - maxVisible + 1);
      }
      for (let i = start; i <= end; i += 1) {
        pages.push(i);
      }
      return pages;
    },
  },
  watch: {
    '$route.params.id'() {
      this.currentPage = 1;
      this.fetchAttendance();
    },
  },
  mounted() {
    this.fetchAttendance();
  },
  methods: {
    guestDisplayName(row) {
      const parts = [row.firstName, row.lastName].filter(Boolean);
      return parts.length ? parts.join(' ') : '--';
    },
    formatDateTime(value) {
      if (!value) return '--';
      try {
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return value;
        return d.toLocaleString(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      } catch {
        return value;
      }
    },
    formatDuration(minutes) {
      if (minutes === null || minutes === undefined || minutes === '') return '--';
      const n = Number(minutes);
      if (Number.isNaN(n)) return String(minutes);
      if (n < 60) return `${n} min`;
      const h = Math.floor(n / 60);
      const m = n % 60;
      return m ? `${h}h ${m}m` : `${h}h`;
    },
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        this.fetchAttendance();
        const section = this.$el?.querySelector?.('.table-section');
        if (section) {
          section.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    },
    onPageSizeChange() {
      this.currentPage = 1;
      this.fetchAttendance();
    },
    async fetchAttendance() {
      const objectId = this.$route.params.id;
      if (!objectId) return;

      this.isLoading = true;
      try {
        const loadPage = async (page) => {
          const response = await apiService.getOutingAttendance(objectId, {
            page,
            pagesize: this.pageSize,
          });
          const body = response.data || {};
          const rows = Array.isArray(body.data) ? body.data : [];
          const count = parseInt(body.totalcount, 10) || 0;
          const maxPage = count <= 0 ? 1 : Math.max(1, Math.ceil(count / this.pageSize));
          return { rows, count, maxPage };
        };

        let page = this.currentPage;
        let { rows, count, maxPage } = await loadPage(page);
        if (page > maxPage) {
          page = maxPage;
          this.currentPage = page;
          ({ rows, count, maxPage } = await loadPage(page));
        }
        this.records = rows;
        this.totalCount = count;
      } catch (error) {
        if (isNoDataError(error)) {
          this.records = [];
          this.totalCount = 0;
        } else if (isHmsSubscriptionInactiveError(error)) {
          // Subscription inactive — modal shown in App.vue
        } else {
          console.error('Outing attendance fetch failed:', error.response?.data || error);
          this.records = [];
          this.totalCount = 0;
        }
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.outing-attendance-container {
  padding: 0;
  background-color: #f8fafc;
  min-height: calc(100vh - 60px);
  display: flex;
  flex-direction: column;
}

.header-section {
  background: white;
  border-radius: 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  margin-bottom: 0;
  border: none;
  border-bottom: 1px solid #e2e8f0;
  position: sticky;
  top: 0;
  z-index: 100;
  flex-shrink: 0;
  margin-top: 20px;
}

.header-content {
  padding: 16px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
}

.page-title {
  display: flex;
  align-items: center;
  gap: 16px;
}

.title-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, #5b2e8e, #7c3aed);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(91, 46, 142, 0.2);
}

.title-icon svg {
  width: 24px;
  height: 24px;
}

.page-title h1 {
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  line-height: 1.2;
  letter-spacing: -0.025em;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.2s ease;
}

.btn-primary {
  background: linear-gradient(135deg, #5b2e8e, #7c3aed);
  color: white;
  box-shadow: 0 2px 8px rgba(91, 46, 142, 0.25);
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(91, 46, 142, 0.35);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.stats-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  padding: 16px 24px;
  background: white;
  border-bottom: 1px solid #e2e8f0;
  flex-shrink: 0;
}

.stat-card {
  background: white;
  border-radius: 10px;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.stat-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.stat-icon {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #e2e8f0, #cbd5e1);
  color: #64748b;
  flex-shrink: 0;
}

.stat-icon svg {
  width: 20px;
  height: 20px;
}

.stat-icon.active {
  background: linear-gradient(135deg, #5b2e8e, #7c3aed);
  color: white;
  box-shadow: 0 2px 6px rgba(91, 46, 142, 0.2);
}

.stat-content {
  flex: 1;
  min-width: 0;
  text-align: left;
}

.stat-content h3 {
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 2px 0;
  line-height: 1.2;
}

.stat-content p {
  font-size: 12px;
  color: #64748b;
  margin: 0;
  font-weight: 500;
}

.table-section {
  flex: 1;
  padding: 0;
  overflow: hidden;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.table-container {
  background: white;
  border-radius: 0;
  box-shadow: none;
  border: none;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.table-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.98);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100;
  backdrop-filter: blur(2px);
  border-radius: 16px;
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 32px;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #e2e8f0;
  border-top: 4px solid #5b2e8e;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  box-shadow: 0 4px 12px rgba(91, 46, 142, 0.2);
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.loading-text {
  color: #64748b;
  font-size: 16px;
  font-weight: 500;
  margin: 0;
}

.table-wrapper {
  overflow-x: auto;
  flex: 1;
}

.complaint-table {
  width: 100%;
  border-collapse: collapse;
}

.complaint-table thead {
  background: #f8fafc;
  position: sticky;
  top: 0;
  z-index: 10;
}

.complaint-table th {
  padding: 12px 16px;
  text-align: left;
  font-size: 14px;
  font-weight: 600;
  color: #475569;
  border-bottom: 2px solid #e2e8f0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
  vertical-align: middle;
}

.complaint-table td {
  padding: 12px 16px;
  font-size: 14px;
  color: #1e293b;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
  text-align: left;
  vertical-align: middle;
}

.complaint-table td.col-desc {
  white-space: normal;
  max-width: 280px;
  line-height: 1.4;
}

.table-row {
  transition: all 0.2s ease;
}

.table-row:hover {
  background: #f8fafc;
  transform: scale(1.001);
}

.empty-row {
  text-align: center;
}

.empty-message {
  padding: 48px 24px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  color: #64748b;
}

.empty-illustration-img {
  width: 250px;
  height: 250px;
  max-width: 100%;
  object-fit: contain;
  margin-bottom: 16px;
}

.pagination-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 24px;
  padding: 24px 32px;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
}

.pagination-left {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px 24px;
}

.pagination-info {
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
}

.page-size-label {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: #475569;
  font-weight: 500;
}

.page-size-label-text {
  white-space: nowrap;
}

.page-size-select {
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  color: #1e293b;
  cursor: pointer;
  outline: none;
  min-width: 72px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.page-size-select:focus {
  border-color: #5b2e8e;
  box-shadow: 0 0 0 3px rgba(91, 46, 142, 0.1);
}

.page-size-select:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pagination-btn {
  min-width: 40px;
  height: 40px;
  padding: 0 14px;
  border: 1px solid #e2e8f0;
  background: white;
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.pagination-btn:hover:not(:disabled) {
  border-color: #5b2e8e;
  color: #5b2e8e;
  background: white;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(91, 46, 142, 0.2);
}

.pagination-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  background: #f8fafc;
}

.pagination-btn.active {
  background: linear-gradient(135deg, #5b2e8e, #7c3aed);
  color: white;
  border-color: #5b2e8e;
  box-shadow: 0 4px 14px rgba(91, 46, 142, 0.3);
}

@media (max-width: 480px) {
  .empty-illustration-img {
    width: 180px;
    height: 180px;
  }
}
</style>
